import React, { useEffect, useState, useRef } from 'react';
import { Button, Modal, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseCircleFilled, CheckCircleFilled, CameraOutlined } from '@ant-design/icons';
import { isMobileOnly } from 'react-device-detect';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import LoaderLogo from '../../components/LoaderLogo';
import Webcam from 'react-webcam';

const { Text } = Typography;

type Props = {
	env: any;
	userMortgageId: any;
	setStages: any;
	setOpenModals: any;
	platform: string;
	traceSadabdell: any;
};

export type ReduxStore = {
	auth: StoreAuth;
};

export type StoreAuth = {
	token: string;
	roles: string;
	operations: any;
};

const PLATFORM_CONFIG: any = {
	gibobs: {
		color: '#748EA0',
		backgroundColor: '#02C3CD'
	},
	sabadell: {
		color: '#212121'
	},
	engel: {
		color: '#666666',
		backgroundColor: '#262626',
		buttonHeight: 40,
		borderRadius: 2
	},
	safecredit: {
		color: '#505763',
		backgroundColor: '#002087',
		buttonHeight: 'auto',
		borderRadius: 4
	}
};

function ModalsDni({
	env,
	userMortgageId,
	setStages,
	setOpenModals,
	platform,
	traceSadabdell
}: Props) {
	const { t, i18n } = useTranslation();
	const { owner } = useParams<any>();
	const [modal2, setModal2] = useState(false);
	const [saveFileDataField, setSaveFileDataField] = useState([]);
	const [modal3, setModal3] = useState(false);
	const userToken = useSelector<ReduxStore, string>((state) => state.auth.token);
	const [loading, setLoading] = useState(false);
	const [imageUrl, setImageUrl] = useState(undefined);
	const [showImage, setShowImage] = useState(undefined);
	const [count, setCount] = useState(0);
	const handleFileInput = useRef<any>({});

	useEffect(() => {
		(async () => {
			try {
				if (imageUrl !== undefined) {
					const response2 = await axios.get(
						`${env.api.url}/v1/documents/get-document-url/${imageUrl.id}`,
						{
							headers: { Authorization: `Bearer ${userToken}` }
						}
					);
					setShowImage(response2.data.data);
				}
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		})();
	}, [imageUrl]);

	useEffect(() => {
		setModal2(true);
		saveData();
		if (platform === 'sabadell') {
			traceSadabdell('paso dni foto', i18n.language === 'cat' ? 'ca' : 'es');
		}
	}, []);

	const saveData = () => {
		const key = 'viewModalsAutomatic';
		const participant = owner;
		const value = true;
		axios.post(
			`${env.api.url}/v1/mortgages/data/update/${userMortgageId}`,
			{ key, participant, value },
			{
				headers: { Authorization: `Bearer ${userToken}` }
			}
		);
	};

	const saveFileData = (file: any, name: any) => {
		const documentFile = saveFileDataField;
		documentFile.push({
			file,
			fileName: name
		});
		setSaveFileDataField(documentFile);
		handleUploadFile(documentFile);
	};

	const handleUploadFile = (file: any) => {
		setLoading(true);
		setSaveFileDataField([]);
		axios
			.put(
				`${env.api.url}/v1/documents/operation/attachment`,
				{
					operationId: userMortgageId,
					file: file[0].file,
					fileName: file[0].fileName,
					owner: owner,
					type: 'nif',
					page: 't'
				},
				{
					headers: { Authorization: `Bearer ${userToken}` }
				}
			)
			.then((response) => {
				setImageUrl(response.data.data);
				setLoading(false);
				if (platform === 'sabadell') {
					setModal2(false);
					setModal3(true);
				}
			})
			.catch((error) => console.log(error));
	};

	const handleSecondPhoto = () => {
		setCount(count + 1);
		if (platform !== 'sabadell') {
			if (count < 1) {
				setShowImage(false);
			} else {
				setStages();
			}
		} else {
			if (count < 1) {
				setModal3(false);
				setModal2(true);
			} else {
				setModal3(false);
				setStages();
			}
		}
	};

	const videoConstraints = {
		width: 1280,
		height: 720,
		facingMode: 'user'
	};

	if (loading) {
		if (platform === 'engel' || platform === 'safecredit') {
			return <Spin spinning={true} fullscreen />;
		} else {
			return (
				<div
					style={{
						textAlign: 'center',
						height: '50vh',
						backgroundColor: platform === 'engel' ? '#FCFAF5' : 'white'
					}}>
					<LoaderLogo />
				</div>
			);
		}
	}

	const handleClick = () => {
		handleFileInput.current.click();
	};

	async function encodeFileAsBase64URL(file: any) {
		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.addEventListener('loadend', () => {
				resolve(reader.result);
			});
			reader.readAsDataURL(file);
		});
	}

	const handleImageChange = async (event: any) => {
		const base64URL = await encodeFileAsBase64URL(event.target.files[0]);
		let tmpImg = undefined;
		tmpImg = (base64URL as string).split(',')[1];
		saveFileData(tmpImg, 'dni');
	};

	const deleteDocumentModal = (imageUrl: any) => {
		axios.get(`${env.api.url}/v1/documents/delete-document/${imageUrl.id}`, {
			headers: { Authorization: `Bearer ${userToken}` }
		});

		if (platform !== 'sabadell') {
			setShowImage(false);
		}
	};

	const renderCameraButtons = () => {
		if (platform === 'gibobs') {
			return (
				<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
					<CloseCircleFilled
						style={{ fontSize: 24, color: '#748EA0' }}
						onClick={() => deleteDocumentModal(imageUrl)}
					/>
					<CheckCircleFilled
						style={{ fontSize: 24, color: '#748EA0' }}
						onClick={() => handleSecondPhoto()}
					/>
				</div>
			);
		} else {
			return (
				<div style={{ display: 'flex', gap: 21, marginTop: 24 }}>
					<Button
						onClick={() => deleteDocumentModal(imageUrl)}
						style={{
							width: '50%',
							borderColor: PLATFORM_CONFIG[platform].backgroundColor,
							color: PLATFORM_CONFIG[platform].backgroundColor,
							height: PLATFORM_CONFIG[platform].buttonHeight,
							borderRadius: PLATFORM_CONFIG[platform].borderRadius
						}}>
						{t('viability.repeat')}
					</Button>
					<Button
						onClick={() => handleSecondPhoto()}
						type="primary"
						className={'button-primari-gibobs'}
						style={{
							width: '50%',
							backgroundColor: PLATFORM_CONFIG[platform].backgroundColor,
							color: '#FFFFFF',
							height: PLATFORM_CONFIG[platform].buttonHeight,
							borderRadius: PLATFORM_CONFIG[platform].borderRadius
						}}>
						{t('offers.accept')}
					</Button>
				</div>
			);
		}
	};

	const renderDesktopContent = () => {
		switch (platform) {
			case 'sabadell':
				return (
					<Webcam
						audio={false}
						height={300}
						width={400}
						videoConstraints={videoConstraints}
						screenshotFormat="image/jpeg"
						forceScreenshotSourceSize={true}>
						{({ getScreenshot, size }) => (
							<div style={{ display: 'flex', justifyContent: 'center' }}>
								<Button
									className="button-primari-gibobs"
									type="primary"
									onClick={() => {
										let imageSrc = getScreenshot();
										imageSrc = imageSrc?.replace('data:image/jpeg;base64,', '');
										saveFileData(imageSrc, 'dni');
									}}>
									{t('take.photo')}
								</Button>
							</div>
						)}
					</Webcam>
				);
			case 'gibobs':
			case 'engel':
			case 'safecredit':
				return (
					<>
						{!showImage ? (
							<Webcam
								audio={false}
								height={283}
								width={420}
								videoConstraints={videoConstraints}
								screenshotFormat="image/jpeg"
								forceScreenshotSourceSize={true}>
								{({ getScreenshot, size }) => (
									<div style={{ display: 'flex', justifyContent: 'center' }}>
										<div
											onClick={() => {
												let imageSrc = getScreenshot();
												imageSrc = imageSrc?.replace('data:image/jpeg;base64,', '');
												saveFileData(imageSrc, 'dni');
											}}
											style={{
												width: 40,
												height: 40,
												borderRadius: 50,
												backgroundColor: PLATFORM_CONFIG[platform].backgroundColor,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												cursor: 'pointer'
											}}>
											<CameraOutlined style={{ fontSize: 24, color: '#fff' }} />
										</div>
									</div>
								)}
							</Webcam>
						) : (
							<div style={{ height: 'auto' }}>
								<img
									style={{ width: 420, height: 283, marginTop: 24 }}
									src={showImage.url}
									alt="asdf"></img>
								{renderCameraButtons()}
							</div>
						)}
					</>
				);
		}
	};

	return (
		<div>
			<Modal
				className={isMobileOnly ? 'ModalMobileFooterColum' : 'ModalDesktopFooterColum'}
				open={modal2}
				onCancel={() => {
					setModal2(false);
					setOpenModals(false);
				}}
				maskClosable={false}
				width={isMobileOnly ? 328 : platform !== 'sabadell' ? 504 : 480}
				title={count === 0 ? t('dni.photo1') : t('dni.photo2')}
				cancelButtonProps={{
					style: { display: 'none' }
				}}
				okButtonProps={{
					style: { display: 'none' }
				}}
				destroyOnClose={true}
				closable={true}>
				<div style={{ marginTop: platform === 'sabadell' && 40 }}>
					<Text style={{ color: PLATFORM_CONFIG[platform].color, fontSize: 14, fontWeight: 400 }}>
						{t('dni.instructionMessage')}
					</Text>
				</div>
				{isMobileOnly ? (
					<div style={{ marginTop: 50, display: 'flex', justifyContent: 'center' }}>
						<input
							type="file"
							accept="image/jpeg"
							capture="environment"
							style={{ display: 'none' }}
							ref={handleFileInput}
							onChange={handleImageChange}
						/>
						<Button className="button-primari-gibobs" type="primary" onClick={handleClick}>
							{t('take.photo')}
						</Button>
					</div>
				) : (
					<>{renderDesktopContent()}</>
				)}
			</Modal>

			<Modal
				className={isMobileOnly ? 'ModalCommonSmall' : 'ModalCommon'}
				open={modal3}
				maskClosable={false}
				style={{ height: isMobileOnly ? undefined : 656 }}
				onCancel={() => {
					setModal3(false);
					setModal2(true);
					deleteDocumentModal(imageUrl);
				}}
				onOk={() => {
					handleSecondPhoto();
				}}
				width={isMobileOnly ? 328 : 480}
				okText={<span>{t('offers.accept')}</span>}
				title={count === 0 ? t('dni.photo1') : t('dni.photo2')}
				cancelText={<span>{t('viability.repeat')}</span>}
				cancelButtonProps={{
					className: platform !== 'engel' ? 'button-secundary-gibobs' : '',
					style: { width: '49%', height: 40 }
				}}
				okButtonProps={{
					className: platform !== 'engel' ? 'button-primari-gibobs' : '',
					style: { width: '49%', height: 40 }
				}}
				destroyOnClose={true}
				closable={true}>
				<div style={{ marginBottom: isMobileOnly ? -10 : 10, marginTop: -16 }}>
					<Text style={{ color: PLATFORM_CONFIG[platform].color, fontSize: 14, fontWeight: 400 }}>
						{t('modalDocuments3.desc')}
					</Text>
				</div>
				<div style={{ marginTop: 30 }}>
					{showImage !== undefined && (
						<div style={{ height: 'auto' }}>
							<img
								style={{ width: isMobileOnly ? 260 : 400, height: isMobileOnly ? 300 : 'auto' }}
								src={showImage.url}
								alt="asdf"></img>
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
}

export default ModalsDni;
